import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';
import {
  XMarkIcon,
  CheckCircleIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import './BuyTokens.css';

export const BuyTokens = ({ onClose, onPurchaseSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('bank_transfer');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentProof, setPaymentProof] = useState(null);
  const [usdToPkrRate, setUsdToPkrRate] = useState(278);

  useEffect(() => {
    loadPackagesAndMethods();
  }, []);

  const loadPackagesAndMethods = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/auth/token-packages');
      
      if (response.data.packages) {
        setPackages(response.data.packages);
        setSelectedPackage(response.data.packages.find(p => p.is_popular) || response.data.packages[0]);
      }
      
      if (response.data.paymentMethods) {
        setPaymentMethods(response.data.paymentMethods.filter(m => m.enabled));
        setSelectedPaymentMethod(response.data.paymentMethods[0]?.id || 'bank_transfer');
      }
      
      if (response.data.usdToPkrRate) {
        setUsdToPkrRate(response.data.usdToPkrRate);
      }
    } catch (error) {
      console.error('Failed to load token packages:', error);
      toast.error('Failed to load token packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF) or PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setPaymentProof(file);
    toast.success('Payment proof selected');
  };

  const handleInitiatePurchase = async (e) => {
    e.preventDefault();

    if (!selectedPackage) {
      toast.error('Please select a token package');
      return;
    }

    if (!selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!paymentProof) {
      toast.error('Please upload payment proof');
      return;
    }

    try {
      setPurchasing(true);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('package_id', selectedPackage.id);
      formData.append('payment_method', selectedPaymentMethod);
      formData.append('payment_proof', paymentProof);

      const response = await apiClient.post('/auth/purchase-tokens', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('✅ Payment submitted! Admin will verify and credit your account shortly.');
      setShowPaymentModal(false);
      setPaymentProof(null);
      setSelectedPackage(null);
      
      if (onPurchaseSuccess) {
        onPurchaseSuccess(response.data);
      }
      
      // Close after a short delay
      setTimeout(onClose, 2000);
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payment');
    } finally {
      setPurchasing(false);
    }
  };

  const calculatePrice = (pkg) => {
    const pricePkr = pkg.price_usd * usdToPkrRate;
    const discountedPrice = pricePkr * (1 - pkg.discount_percentage / 100);
    return {
      original: Math.round(pricePkr * 100) / 100,
      discounted: Math.round(discountedPrice * 100) / 100,
      savings: Math.round((pricePkr - discountedPrice) * 100) / 100
    };
  };

  const getPaymentDetails = (methodId) => {
    const amount = selectedPackage ? Math.round(calculatePrice(selectedPackage).discounted) : 0;
    
    const paymentDetails = {
      easypaisa: {
        icon: '🟦',
        name: 'EasyPaisa',
        number: '03000047478',
        accountHolder: 'Muhammad Yousaf Iqbal',
        amount: `PKR ${amount.toLocaleString()}`
      },
      jazzcash: {
        icon: '🟪',
        name: 'Jazz Cash',
        number: '03000047478',
        accountHolder: 'Muhammad Yousaf Iqbal',
        amount: `PKR ${amount.toLocaleString()}`
      },
      bank_transfer: {
        icon: '🏦',
        name: 'Standard Chartered Bank Pakistan',
        number: '03000047478',
        label: 'Rasst ID',
        accountHolder: 'Muhammad Yousaf Iqbal',
        amount: `PKR ${amount.toLocaleString()}`
      }
    };
    
    return paymentDetails[methodId];
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-6 w-6" />
            <h2 className="text-2xl font-bold">Buy SRO Chat Tokens</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-blue-500 rounded-lg transition"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Rate */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              💱 <strong>Exchange Rate:</strong> 1 USD = PKR {usdToPkrRate.toLocaleString()}
            </p>
          </div>

          {/* Token Packages */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" />
              Choose Your Token Package
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packages.map((pkg) => {
                const price = calculatePrice(pkg);
                const isSelected = selectedPackage?.id === pkg.id;

                return (
                  <button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg)}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {pkg.is_popular && (
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full">
                          ⭐ Most Popular
                        </span>
                      </div>
                    )}

                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                        <p className="text-2xl font-bold text-blue-600 mt-2">
                          {(pkg.tokens / 1000).toLocaleString()}K
                        </p>
                        <p className="text-sm text-gray-600 mt-2">Tokens</p>
                      </div>
                      {isSelected && (
                        <CheckCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                      )}
                    </div>

                    {/* Pricing */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">Price</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-xl font-bold text-gray-900">
                          PKR {price.discounted.toLocaleString()}
                        </span>
                        {price.savings > 0 && (
                          <>
                            <span className="text-sm line-through text-gray-400">
                              {price.original.toLocaleString()}
                            </span>
                            <span className="text-xs font-semibold text-green-600">
                              Save {pkg.discount_percentage}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 mt-3">{pkg.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Payment Method Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCardIcon className="h-5 w-5 text-blue-600" />
              Select Payment Method
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedPaymentMethod(method.id)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedPaymentMethod === method.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{method.icon}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{method.name}</p>
                      <p className="text-xs text-gray-600">{method.description}</p>
                    </div>
                    {selectedPaymentMethod === method.id && (
                      <CheckCircleIcon className="h-5 w-5 text-blue-600 ml-auto" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleInitiatePurchase}
              disabled={!selectedPackage}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg font-medium text-white hover:from-blue-700 hover:to-blue-800 transition disabled:opacity-50"
            >
              Continue to Payment
            </button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-6 flex items-center justify-between">
              <h3 className="text-xl font-bold">Confirm Payment</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-1 hover:bg-green-500 rounded-lg transition"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitPayment} className="p-6 space-y-4">
              {/* Order Summary */}
              {selectedPackage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">{selectedPackage.name}</span>
                      <span className="font-semibold text-gray-900">
                        {(selectedPackage.tokens / 1000).toLocaleString()}K tokens
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Payment Method</span>
                      <span className="font-semibold text-gray-900">
                        {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                      </span>
                    </div>
                    <div className="border-t border-green-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-900">Total Amount</span>
                        <span className="text-lg font-bold text-green-600">
                          PKR {(calculatePrice(selectedPackage).discounted).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details */}
              {selectedPaymentMethod && selectedPackage && (() => {
                const details = getPaymentDetails(selectedPaymentMethod);
                return details ? (
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-lg p-4 space-y-3">
                    <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                      {details.icon} {details.name}
                    </h4>
                    
                    <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{details.label || 'Account Number'}:</span>
                        <span className="font-bold text-gray-900 font-mono">{details.number}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Account Holder:</span>
                        <span className="font-semibold text-gray-900">{details.accountHolder}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                        <span className="font-bold text-gray-900">Amount to Pay:</span>
                        <span className="text-lg font-bold text-green-600">{details.amount}</span>
                      </div>
                    </div>

                    {/* Important Instructions */}
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                      <h5 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                        📱 Important Instructions:
                      </h5>
                      <ul className="text-xs text-yellow-900 space-y-1 list-disc list-inside">
                        <li>Transfer the payment using the details above</li>
                        <li>Take a screenshot of the payment receipt/confirmation</li>
                        <li>Send the screenshot to WhatsApp: <strong>+923080323323</strong></li>
                        <li>Mention your email or business name in the message</li>
                        <li>Your subscription will be activated within 1-2 hours</li>
                      </ul>
                    </div>

                    {/* WhatsApp Support */}
                    <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                      <p className="text-sm text-green-900 font-semibold flex items-center gap-2">
                        💬 WhatsApp Support
                      </p>
                      <p className="text-xs text-green-800 mt-1">
                        Send payment proof to: <strong>+923080323323</strong>
                      </p>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Payment Proof Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  📸 Upload Payment Proof *
                </label>
                <label className="relative cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handlePaymentProofUpload}
                    className="hidden"
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 hover:bg-blue-50 transition">
                    <p className="text-sm text-gray-600">
                      {paymentProof ? (
                        <>
                          ✅ {paymentProof.name}
                        </>
                      ) : (
                        <>
                          Click to upload or drag and drop
                          <br />
                          <span className="text-xs text-gray-500">
                            (JPG, PNG, GIF, or PDF - Max 5MB)
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                </label>
              </div>

              {/* Note */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ Your tokens will be credited after admin verification of payment proof.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                  disabled={purchasing}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!paymentProof || purchasing}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 rounded-lg font-medium text-white hover:from-green-700 hover:to-green-800 transition disabled:opacity-50"
                >
                  {purchasing ? '⏳ Submitting...' : '✓ Submit Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyTokens;
